import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  IconButton,
  Typography,
  AppBar,
  Toolbar,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
  useMediaQuery,
  Drawer,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  ArrowBack as ArrowBackIcon,
  AssignmentInd,
  Settings as SettingsIcon,
  Help as HelpIcon,
  AttachMoney,
  Shield,
  Balance,
  ForkRight as ForkRightIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { Tabs as AppTabs } from '../../layout/MainLayout'
import BacktestTable from '../../app/reports/components/backtestResults/BacktestTable'
import BacktestGradeIcon from './BacktestGradeIcon'
import BacktestEquityChart from './BacktestEquityChart'
import PatternVersionTitle from './PatternVersionTitle'
import BacktestRatioSelect from './BacktestRatioSelect'
import { sortSummariesByProfit } from '../../common/utils/backtestSummaryStats'
import BacktestViewPanelContainer from '../../layout/BacktestViewPanelContainer'
import FavoriteBacktestIconButton from './FavoriteBacktestIconButton'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AssessmentIcon from '@mui/icons-material/Assessment'
import TradeDurationChart from './TradeDurationChart'
import TradeTimeAnalysis from './TradeTimeAnalysis'
import TradesTable from './TradesTable'
import { MonteCarloResponse } from '../../types/monteCarlo'
import PermissionOverlay from '../PermissionOverlay'
import RankCards from './RankCards'
import { httpsCallable } from 'firebase/functions'
import { firebase_functions } from '../../common/firebaseConfig'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import ColumnGroupToggle from './ColumnGroupToggle'
import { useBacktestRankings } from '../../hooks/useBacktestRankings'
import ChatAssistant from './ChatAssistant'
import { usePattern } from '../../hooks/usePattern'
import { captureEvent } from '../../posthog'

interface MonteCarloRequest {
  backtestId: string
  tradeGroup: string
  iterations?: number
  riskFraction?: number
  mode?: 'shuffle' | 'bootstrap' | 'block-bootstrap'
  blockSize?: number
  seed?: number
  ruinThreshold?: number
  histBins?: number
  quantiles?: number[]
  startEquity?: number
  returnKind?: 'summary' | 'full'
  sampleCurves?: number
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `insights-tab-${index}`,
    'aria-controls': `insights-tabpanel-${index}`,
  }
}

// Help Dialog Component
function CategoryHelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const categories = [
    {
      icon: <AttachMoney sx={{ color: '#9C27B0' }} />,
      title: 'Best Expectancy',
      description: 'Highest average R/trade',
      explanation:
        'Expectancy measures the average profit or loss per trade in R-multiples. R-multiples are calculated as price movement divided by stop loss distance. The expectancy formula is: (Average Win R × Win Rate) + (Average Loss R × Loss Rate). A positive expectancy means the strategy is profitable on average.',
    },
    {
      icon: <Shield sx={{ color: '#FF9800' }} />,
      title: 'Most Robust',
      description: 'Best Monte-Carlo robustness score',
      explanation:
        'Robustness measures how consistent the strategy performs across different market conditions using Monte Carlo analysis. The score combines four metrics: 5th percentile profit (40%), 95th percentile max drawdown (30%), risk of ruin probability (20%), and final equity IQR (10%). Higher scores indicate more robust performance.',
    },
    {
      icon: <Balance sx={{ color: '#2196F3' }} />,
      title: 'Best Risk-Adjusted Return',
      description: 'Best MAR ratio',
      explanation:
        "Risk-adjusted return measures how much profit you get relative to the risk taken using a MAR (Minimum Acceptable Return) ratio. It's calculated as Profit Percentage ÷ Maximum Relative Drawdown. Higher values indicate better risk-adjusted performance.",
    },
  ]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography
          variant="h6"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <HelpIcon color="primary" />
          Category Explanations
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Each category represents a different way to evaluate trading strategy performance.
          Understanding these metrics helps you choose the strategy that best fits your trading
          goals.
        </Typography>
        <List>
          {categories.map((category, index) => (
            <ListItem key={index} sx={{ alignItems: 'flex-start', py: 2 }}>
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{category.icon}</ListItemIcon>
              <ListItemText
                primary={
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {category.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {category.description}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {category.explanation}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function BacktestEdit({ backtestToView, goBack }: any) {
  const theme = useTheme()
  const {
    usermeta,
    savedBacktestsMap,
    canViewBacktestInsights,
    setActiveTab,
    backtestNameOverrides,
    renameBacktestLocally,
  } = useApplicationSuiteContext()

  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  const [selectedRatioGroup, setSelectedRatioGroup] = useState<string>('')
  const [insightsTabValue, setInsightsTabValue] = useState(0)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [monteCarloResults, setMonteCarloResults] = useState<{
    [group: string]: MonteCarloResponse
  }>({})
  const [monteCarloLoading, setMonteCarloLoading] = useState(false)
  const [visibleGroups, setVisibleGroups] = useState<string[]>(['essentials']) // Default to showing essentials and basics (trade ratio is always visible)
  const [helpDialogOpen, setHelpDialogOpen] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editName, setEditName] = useState('')
  const [renameBacktestCallable, renameBacktestLoading] = useHttpsCallable(
    firebase_functions,
    'renameBacktest'
  )

  const [exitPattern] = usePattern(
    backtestToView?.backtestSettings?.exitPatternId,
    backtestToView?.backtestSettings?.exitPatternVersion,
    backtestToView?.backtestSettings?.gcid || backtestToView?.gcid
  )

  // Calculate rankings using the custom hook
  const { summariesWithRankings, rankingResults } = useBacktestRankings(
    backtestToView,
    undefined, // tradesByGroup
    monteCarloResults
  )

  // Get available trade groups from backtest summaries
  const availableGroups = React.useMemo(() => {
    if (!backtestToView?.summaries) return []
    return backtestToView.summaries
      .filter((summary: any) => summary.closedTradeCount > 0)
      .sort((a: any, b: any) => (b.currentProfit || 0) - (a.currentProfit || 0))
      .map((summary: any) => summary.group)
  }, [backtestToView?.summaries])

  const summariesWithTrades = React.useMemo(
    () =>
      sortSummariesByProfit(
        (backtestToView?.summaries || []).filter((summary: any) => summary.closedTradeCount > 0)
      ),
    [backtestToView?.summaries]
  )

  // Top ratios by profit as faint context, plus whichever ratio is selected.
  const tradeGroupsToGraph = React.useMemo(() => {
    const top = summariesWithTrades.slice(0, 5).map((summary: any) => summary.group)
    return selectedRatioGroup && !top.includes(selectedRatioGroup)
      ? [...top, selectedRatioGroup]
      : top
  }, [summariesWithTrades, selectedRatioGroup])

  // Set default selected group to the most profitable one
  useEffect(() => {
    if (availableGroups.length > 0 && !selectedRatioGroup) {
      setSelectedRatioGroup(availableGroups[0])
    }
  }, [availableGroups, selectedRatioGroup])

  const handleRatioGroupChange = (event: SelectChangeEvent) => {
    setSelectedRatioGroup(event.target.value)
  }

  const handleInsightsTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setInsightsTabValue(newValue)
  }

  const handleToggleGroup = (group: string) => {
    setVisibleGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    )
  }

  const handleOpenHelpDialog = () => {
    setHelpDialogOpen(true)
  }

  const handleCloseHelpDialog = () => {
    setHelpDialogOpen(false)
  }

  // Handle mounting for animation
  useEffect(() => {
    if (isPanelOpen) {
      setMounted(true)
    }
  }, [isPanelOpen])

  // Function to run Monte Carlo analysis for a specific trade group
  const runMonteCarloForGroup = useCallback(
    async (group: string): Promise<MonteCarloResponse | null> => {
      try {
        const monteCarloFn = httpsCallable(firebase_functions, 'monteCarlo')

        const request: MonteCarloRequest = {
          backtestId: backtestToView.id,
          tradeGroup: group,
          iterations: 5000,
          mode: 'block-bootstrap',
          returnKind: 'summary',
          sampleCurves: 10,
          seed: 262,
        }

        const result = await monteCarloFn(request)
        return result.data as MonteCarloResponse
      } catch (err: any) {
        console.error(`Monte Carlo error for group ${group}:`, err)
        return null
      }
    },
    [backtestToView.id]
  )

  // Function to run Monte Carlo analysis for all summaries
  const runMonteCarloForAllSummaries = useCallback(async () => {
    if (!backtestToView?.summaries || !canViewBacktestInsights) return

    setMonteCarloLoading(true)
    const results: { [group: string]: MonteCarloResponse } = {}

    // Get all groups with trades
    const groupsWithTrades = backtestToView.summaries
      .filter((summary: any) => summary.closedTradeCount > 0)
      .map((summary: any) => summary.group)

    // Run Monte Carlo for each group in parallel
    const promises = groupsWithTrades.map(async (group: string) => {
      const result = await runMonteCarloForGroup(group)
      if (result) {
        results[group] = result
      }
    })

    await Promise.all(promises)
    setMonteCarloResults(results)
    setMonteCarloLoading(false)
  }, [backtestToView?.summaries, canViewBacktestInsights, runMonteCarloForGroup])

  // Run Monte Carlo analysis for all summaries when component mounts
  useEffect(() => {
    if (backtestToView?.summaries && canViewBacktestInsights) {
      runMonteCarloForAllSummaries()
    }
  }, [backtestToView?.summaries, canViewBacktestInsights, runMonteCarloForAllSummaries])

  const handleOpenPanel = () => {
    setIsPanelOpen(true)
  }

  const handleClosePanel = () => {
    setIsPanelOpen(false)
  }

  const viewedBacktestId = backtestToView?.id
  useEffect(() => {
    if (!viewedBacktestId) return
    captureEvent('backtest_viewed', {
      is_own: backtestToView?.gcid === usermeta?.gcid,
      status: backtestToView?.status,
    })
    // Once per opened backtest, not on every snapshot update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedBacktestId])

  if (!backtestToView) return null

  // Panel content for both mobile drawer and desktop slide
  const panelContent = (
    <BacktestViewPanelContainer
      backtest={backtestToView}
      goBack={goBack}
      onClose={handleClosePanel}
    />
  )

  return (
    <Box
      sx={{
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <Box display={'flex'} overflow={'hidden'} height={'100%'}>
        {/* Left Column: Main Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'hidden',
            width: isMobile ? '100%' : 'calc(100% - 400px)',
            position: 'relative',
          }}
        >
          {/* Header Section */}
          <AppBar
            position="static"
            sx={{
              boxShadow: 'none',
              background: 'none',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={goBack}
                sx={{ mr: 2 }}
                aria-label="back to backtest list"
              >
                <ArrowBackIcon />
              </IconButton>

              {backtestToView ? (
                <Box sx={{ width: '100%' }}>
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    width={'100%'}
                    alignItems={'center'}
                  >
                    <Box
                      overflow={'hidden'}
                      display={'flex'}
                      alignItems={'center'}
                      gap={1}
                      flex={1}
                      minWidth={0}
                    >
                      <AssessmentIcon fontSize={'small'} sx={{ flexShrink: 0 }} />
                      {isEditingName ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            maxWidth: '100%',
                          }}
                        >
                          <Box sx={{ position: 'relative', minWidth: 40, maxWidth: '100%' }}>
                            {/* invisible sizer — absolutely positioned so it only drives width, not height */}
                            <Box
                              component="span"
                              aria-hidden
                              sx={{
                                visibility: 'hidden',
                                whiteSpace: 'pre',
                                fontSize: '0.9rem',
                                fontFamily: 'inherit',
                                px: '14px',
                                display: 'block',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                height: 0,
                                pointerEvents: 'none',
                              }}
                            >
                              {editName || ' '}
                            </Box>
                            <TextField
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              size="small"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  renameBacktestCallable({
                                    backtestId: backtestToView.id,
                                    name: editName,
                                  }).then(() => {
                                    renameBacktestLocally(backtestToView.id, editName)
                                    setIsEditingName(false)
                                  })
                                } else if (e.key === 'Escape') {
                                  setIsEditingName(false)
                                }
                              }}
                              sx={{
                                width: '100%',
                                '& .MuiInputBase-input': { fontSize: '0.9rem' },
                              }}
                            />
                          </Box>
                          <IconButton
                            size="small"
                            disabled={renameBacktestLoading}
                            onClick={() =>
                              renameBacktestCallable({
                                backtestId: backtestToView.id,
                                name: editName,
                              }).then(() => {
                                renameBacktestLocally(backtestToView.id, editName)
                                setIsEditingName(false)
                              })
                            }
                          >
                            {renameBacktestLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton size="small" onClick={() => setIsEditingName(false)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            color="text.primary"
                            overflow={'hidden'}
                            whiteSpace={'nowrap'}
                            textOverflow={'ellipsis'}
                          >
                            {backtestNameOverrides[backtestToView.id] ??
                              backtestToView.backtestSettings.name}
                          </Typography>
                          {backtestToView.gcid === usermeta?.gcid && (
                            <IconButton
                              size="small"
                              sx={{ color: 'text.secondary', flexShrink: 0 }}
                              onClick={() => {
                                setEditName(
                                  backtestNameOverrides[backtestToView.id] ??
                                    backtestToView.backtestSettings.name
                                )
                                setIsEditingName(true)
                              }}
                            >
                              <EditIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                          )}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BacktestGradeIcon backtest={backtestToView} />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ overflow: 'hidden' }}>
                      <Box display={'flex'} alignItems={'center'} gap={1}>
                        <AccountTreeIcon
                          fontSize={'small'}
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        />
                        <PatternVersionTitle
                          pattern={backtestToView.pattern}
                          symbol={backtestToView.backtestSettings.executionSymbol}
                          color="text.secondary"
                          onClick={() =>
                            setActiveTab({
                              tab: AppTabs.PatternBuilder,
                              options: { item: backtestToView.pattern },
                            })
                          }
                        />
                      </Box>
                    </Box>

                    <Box display={'flex'} alignItems={'center'} gap={1}>
                      {savedBacktestsMap[backtestToView.id] && (
                        <FavoriteBacktestIconButton
                          backtest={backtestToView}
                          size={'small'}
                          style={{ padding: 0 }}
                        />
                      )}
                      {backtestToView.gcid === usermeta.gcid && (
                        <AssignmentInd fontSize={'small'} color={'primary'} />
                      )}
                    </Box>
                  </Box>

                  {exitPattern && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Box sx={{ overflow: 'hidden' }}>
                        <Box display={'flex'} alignItems={'center'} gap={1}>
                          <AccountTreeIcon
                            fontSize={'small'}
                            sx={{ color: theme.palette.text.secondary }}
                          />
                          <PatternVersionTitle
                            pattern={exitPattern}
                            symbol={backtestToView.backtestSettings.executionSymbol}
                            color="text.secondary"
                            onClick={() =>
                              setActiveTab({
                                tab: AppTabs.PatternBuilder,
                                options: { item: exitPattern },
                              })
                            }
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                  Backtest Not Found
                </Typography>
              )}
            </Toolbar>
          </AppBar>

          {/* Content Section */}
          <Box
            sx={{
              px: 1,
              overflow: 'auto',
              backgroundColor: theme.palette.background.custom1,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              height: 'calc(100% - 64px)',
              pt: 2,
            }}
          >
            {/* Net Profit Chart and Best by Category */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexDirection: isMobile ? 'column' : 'row',
              }}
            >
              {/* Net Profit Chart */}
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  backgroundColor: theme.palette.background.custom2,
                  borderRadius: 2,
                  flex: 2,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h6">Net Profit</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Selected ratio highlighted over the top ratios, with drawdown below
                    </Typography>
                  </Box>
                  {selectedRatioGroup && summariesWithTrades.length > 0 && (
                    <BacktestRatioSelect
                      summaries={summariesWithTrades}
                      selectedGroup={selectedRatioGroup}
                      onChange={setSelectedRatioGroup}
                    />
                  )}
                </Box>
                <Box width={'100%'} height={224} sx={{ position: 'relative' }}>
                  {summariesWithTrades.length > 0 && (
                    <BacktestEquityChart
                      backtestId={backtestToView.id}
                      tradeGroups={tradeGroupsToGraph}
                      accountEquity={backtestToView.backtestSettings.analyzeTrades.equity}
                      highlightGroup={selectedRatioGroup || undefined}
                    />
                  )}
                </Box>
              </Paper>

              {/* Best by Category */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: theme.palette.background.custom2,
                  borderRadius: 2,
                  flex: 1,
                  minWidth: 300,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Typography variant="h6">Best by Category</Typography>
                  <IconButton
                    size="small"
                    onClick={handleOpenHelpDialog}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box>
                  <RankCards
                    backtest={backtestToView}
                    monteCarloLoading={monteCarloLoading}
                    rankingResults={rankingResults}
                  />
                </Box>
              </Paper>
            </Box>

            {/* Trade Ratio Results Table - Full Table */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                backgroundColor: theme.palette.background.custom2,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 3,
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 0 },
                }}
              >
                <Box>
                  <Typography variant="h6">Complete Trade Ratio Results</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Detailed overview of all risk/reward ratios performance
                  </Typography>
                </Box>
                <Box
                  sx={{
                    alignSelf: { xs: 'flex-start', md: 'center' },
                    mt: { xs: 0, md: 0 },
                  }}
                >
                  <ColumnGroupToggle
                    visibleGroups={visibleGroups}
                    onToggleGroup={handleToggleGroup}
                    condensed={isMobile}
                  />
                </Box>
              </Box>
              <BacktestTable
                backtest={backtestToView}
                monteCarloResults={monteCarloResults}
                visibleGroups={visibleGroups}
                summariesWithRankings={summariesWithRankings}
              />
            </Paper>

            {/* Trade Ratio Insights */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                backgroundColor: theme.palette.background.custom2,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  mb: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="h6">Trade Analysis</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Detailed analysis of trade performance for the selected risk/reward ratio
                  </Typography>
                </Box>

                {/* Trade Ratio Selector */}
                <Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Selected Trade Ratio</InputLabel>
                    <Select
                      value={selectedRatioGroup}
                      label="Selected Trade Ratio"
                      onChange={handleRatioGroupChange}
                      disabled={!canViewBacktestInsights}
                    >
                      {availableGroups.map((group: string) => {
                        const tradeRatios =
                          backtestToView.backtestSettings?.generateTrades?.tradeRatios || []
                        const parentRatio = tradeRatios.find(
                          (r: any) => `${r.risk}/${r.reward}` === group
                        )
                        const hasConditions = parentRatio?.conditions?.length > 0
                        const conditionCount = parentRatio?.conditions?.length || 0

                        return (
                          <MenuItem key={group} value={group}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <span>{group} Risk/Reward</span>
                              {hasConditions && (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.25,
                                  }}
                                >
                                  <span style={{ color: 'gray' }}>· {conditionCount}</span>
                                  <ForkRightIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                </Box>
                              )}
                            </Box>
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box>
                <PermissionOverlay
                  isVisible={!canViewBacktestInsights}
                  message="Upgrade to Pro Plus to access detailed trade ratio insights and analysis."
                >
                  {/* Insights Tabs */}
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={insightsTabValue}
                        onChange={handleInsightsTabChange}
                        aria-label="trade insights tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                      >
                        <Tab label="Time-based Analysis" {...a11yProps(0)} />
                        <Tab label="Duration Analysis" {...a11yProps(1)} />
                        <Tab label="All Trades" {...a11yProps(2)} />
                      </Tabs>
                    </Box>

                    <TabPanel value={insightsTabValue} index={0}>
                      <TradeTimeAnalysis
                        backtestId={backtestToView.id}
                        accountEquity={backtestToView.backtestSettings.analyzeTrades.equity}
                        alertId={backtestToView.alertId}
                        selectedGroup={selectedRatioGroup}
                        timeIntervals={[
                          { interval: 1, timeframe: 'hour' },
                          {
                            interval: backtestToView.pattern.trigger.chartDetails.periodicity,
                            timeframe: backtestToView.pattern.trigger.chartDetails.timeframe,
                          },
                        ]}
                      />
                    </TabPanel>
                    <TabPanel value={insightsTabValue} index={1}>
                      <TradeDurationChart
                        backtestId={backtestToView.id}
                        selectedGroup={selectedRatioGroup}
                      />
                    </TabPanel>
                    <TabPanel value={insightsTabValue} index={2}>
                      <TradesTable backtest={backtestToView} selectedGroup={selectedRatioGroup} />
                    </TabPanel>
                  </Box>
                </PermissionOverlay>
              </Box>
            </Paper>
          </Box>

          {/* AI Assistant FAB + Chat - Admin Only */}
          <ChatAssistant backtest={backtestToView} />
        </Box>

        {/* Right Column: Edit Menu - Desktop Only */}
        {!isMobile && <BacktestViewPanelContainer backtest={backtestToView} goBack={goBack} />}

        {/* Mobile Drawer */}
        {isMobile && mounted && (
          <Drawer
            anchor="right"
            open={isPanelOpen}
            onClose={handleClosePanel}
            PaperProps={{
              sx: {
                width: '400px',
                maxWidth: '100%',
                height: '100%',
              },
            }}
          >
            {panelContent}
          </Drawer>
        )}

        {/* Mobile FAB to open panel */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="open settings"
            onClick={handleOpenPanel}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <SettingsIcon />
          </Fab>
        )}
      </Box>

      {/* Help Dialog */}
      <CategoryHelpDialog open={helpDialogOpen} onClose={handleCloseHelpDialog} />
    </Box>
  )
}

export default BacktestEdit
